// The motivation for this is the following:
// APM by default may include text content of clickable elements (e.g. button labels) in its reports
// Since Clinic may have many interactive elements that contain PII, we need to override the default behavior
// Our algorithm is the following:
// Algorithm:
// 1. A capture-phase click listener tracks the last clicked element (capture avoids missing events stopped by propagation).
// 2. When APM requests a transaction name, getUsefulElementName() is called with that element.
// 3. We walk up the DOM from the clicked element, searching for the closest ancestor that carries
//    one of the INTERESTING_ATTRIBUTE_NAMES (checked in priority order, highest first).
// 4. We collect the full path from the clicked element up to that ancestor (inclusive).
// 5. Each element in the path is described as "tagname[attr="value"]" using the first interesting
//    attribute it has. For "class", styled-components-generated suffixes (e.g. "__sc-", "-sc-") are
//    stripped so the name stays stable across builds.
// 6. The path segments are joined with " > " to form a CSS-selector-like string, e.g.:
//    "div[data-testid="sidebar"] > button[aria-label="Close"]"
// If no ancestor with any interesting attribute is found, "unknown element" is returned.

const INTERESTING_ATTRIBUTE_NAMES: string[] = [
  'data-transaction-name', // highest prio
  'name',
  'href',
  'data-testid',
  'data-test-id',
  'aria-label',
  'role',
  'class', // lowest
];

let lastClickedElement: Element | null = null;

document.addEventListener(
  'click',
  event => {
    if (event.target instanceof Element) {
      lastClickedElement = event.target;
    }
  },
  { capture: true }, // avoid propagation stops
);

export function getLastClickedElement() {
  return lastClickedElement;
}

export function getUsefulElementName(element: Element | null) {
  const ancestors = element ? getPathToClosestInterestingAncestor(element) : null;

  if (!ancestors) {
    return 'unknown element';
  }

  return ancestors.map(describeElement).join(' > ');
}

function describeElement(element: Element) {
  let description = element.tagName.toLowerCase();

  for (const attributeName of INTERESTING_ATTRIBUTE_NAMES) {
    let attributeValue = element.getAttribute(attributeName);
    let operator = '=';

    if (attributeName === 'class' && attributeValue) {
      const styledComponentsClassSuffix = /(__|-)sc-/;
      const classParts = attributeValue.split(styledComponentsClassSuffix);

      if (classParts.length > 1) {
        attributeValue = classParts[0];
        operator = '*=';
      }
    }

    if (attributeValue) {
      description += `[${attributeName}${operator}"${attributeValue}"]`;
      break;
    }
  }

  return description;
}

function getPathToClosestInterestingAncestor(element: Element) {
  for (const attributeName of INTERESTING_ATTRIBUTE_NAMES) {
    const predicate = (element: Element) => Boolean(element.getAttribute(attributeName));
    const ancestors = getPathToClosestMatchingAncestor(element, predicate);

    if (ancestors) {
      return ancestors;
    }
  }

  return null;
}

function getPathToClosestMatchingAncestor(initialElement: Element, predicate: (element: Element) => boolean) {
  let currentElement = initialElement;
  const elements: Element[] = [];

  // eslint-disable-next-line no-constant-condition
  while (true) {
    elements.unshift(currentElement);

    if (predicate(currentElement)) {
      return elements;
    }

    if (!currentElement.parentElement) {
      return null;
    }

    currentElement = currentElement.parentElement;
  }
}
